import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Button,
    TextField,
    Typography,
    Box,
    LinearProgress,
    Snackbar,
    Alert,
    Chip,
    InputAdornment,
    Modal
} from '@mui/material';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { getAllStudentAttendence, getTrainerAttendence, markAllStudentAttendence, markTrainerAttendence } from 'helpers/attendance_helper';
import { toast } from 'react-toastify';
import { CircularProgress } from '@material-ui/core';
import { TRAINER_ATTENDANCE_EXCEL } from 'helpers/url_helper';

const TrainerAttendance = ({ trainers, batchDetails }) => {
    const [attendance, setAttendance] = useState(() => {
        const initialAttendance = {};
        trainers?.forEach(trainer => {
            initialAttendance[trainer.id] = false; // Default to present
        });
        return initialAttendance;
    });
    const [date, setDate] = useState(dayjs());
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const params = useParams();
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });



    // Load existing attendance for selected date
    useEffect(() => {
        const fetchAttendance = async () => {
            if (!date || trainers?.length === 0) return;

            try {
                setLoading(true);

                const data = await getTrainerAttendence(`/${params.id}/${date.format('YYYY-MM-DD')}`);
                if (data?.success) {

                    const fetchedAttendance = {};
                    if (data?.result?.length === 0) {

                        trainers?.forEach(trainer => {
                            fetchedAttendance[trainer.id] = false;
                        });
                    } else {
                        data?.result?.forEach(record => {
                            if (record.trainerId) {
                                fetchedAttendance[record.trainerId] = record.status === 'PRESENT';
                            }
                        });
                    }

                    setAttendance(fetchedAttendance);
                }


            } catch (error) {
                showSnackbar('Failed to fetch attendance records', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchAttendance();
    }, [date, params.id, trainers]);

    const handleStatusChange = (trainerId, checked) => {
        setAttendance(prev => ({
            ...prev,
            [trainerId]: checked
        }));
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const records = trainers?.map(trainer => ({
                trainerId: trainer.id,
                batchId: params.id,
                status: attendance[trainer.id] ? 'PRESENT' : 'ABSENT',
                date: date.format('YYYY-MM-DD')
            }));
            const data = await markTrainerAttendence(records);

            if (data?.success) {
                toast.success("Attendance saved successfully");
            } else {
                toast.success("Failed to save attendance");
                showSnackbar('Failed to save attendance', 'error');
                return;
            }

            showSnackbar('Attendance saved successfully', 'success');
        } catch (error) {
            showSnackbar('Failed to save attendance', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const markAll = (status) => () => {
        const newAttendance = {};
        trainers.forEach(trainer => {
            newAttendance[trainer.id] = status;
        });
        setAttendance(newAttendance);
    };

    const presentCount = Object.values(attendance).filter(v => v).length;
    const absentCount = trainers?.length - presentCount;

    const handleDateChange = (e) => {
        const newDate = dayjs(e.target.value);
        if (newDate.isValid() && newDate >= dayjs()) {
            setDate(newDate);
        } else {
            toast.error("You cannot select a past date");
        }
    };

    const [filteredTrainers, setFilteredTrainers] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const handleSearchChange = (value) => {

        setSearchTerm(value);

        if (!value.trim()) {
            setFilteredTrainers(null);
            return;
        }

        const searchLower = value?.toLowerCase();
        const filtered = trainers?.filter(trainer =>
            trainer.id.toString().includes(searchLower) ||
            trainer.name.toLowerCase().includes(searchLower)
        );
        setFilteredTrainers(filtered || []);
    };

    const trainersToDisplay = filteredTrainers !== null ? filteredTrainers : trainers;


    const downloadTrainertAttendanceExcel = async (fromDate, toDate) => {
        try {
            // Construct the dates in 'yyyy-MM-dd' format as expected by your Spring Boot @DateTimeFormat
            const formattedFromDate = fromDate.format('YYYY-MM-DD');
            const formattedToDate = toDate.format('YYYY-MM-DD');

            // Construct the URL for your backend endpoint
            const url = `${TRAINER_ATTENDANCE_EXCEL}${params.id}?fromDate=${formattedFromDate}&toDate=${formattedToDate}`;

            // Make the GET request to your backend
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    // You might need to add authorization headers here if your API is secured
                    // 'Authorization': `Bearer ${yourAuthToken}`,
                },
            });

            // Check if the response was successful
            if (!response.ok) {
                // If the server returns an error, try to read the error message
                const errorText = await response.text();
                throw new Error(`Failed to download Excel: ${response.status} ${response.statusText} - ${errorText}`);
            }

            // Get the Blob (Binary Large Object) from the response
            const blob = await response.blob();

            // Get the filename from the Content-Disposition header
            // Your Spring Boot controller sets this: headers.setContentDispositionFormData("attachment", filename);
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `Batch_${batchDetails.batchName}_trainer_attendance.xlsx`; // Default filename
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            // Create a URL for the blob
            const blobUrl = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename); // Set the download attribute with the filename

            // Append to the body and programmatically click it to trigger download
            document.body.appendChild(link);
            link.click();

            // Clean up: remove the link and revoke the blob URL
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            console.log('Excel file downloaded successfully!');

        } catch (error) {
            console.error('Error downloading Excel file:', error);
            toast.error(`Error: ${error.message || 'Could not download the Excel file.'}`);
            // You can add more sophisticated error handling here, e.g., show a user-friendly message
        }
    };

    const [downloadFromDate, setDownloadFromDate] = useState(dayjs());
    const [downloadToDate, setDownloadToDate] = useState(dayjs());
    const [openDownloadModal, setOpenDownloadModal] = useState(false);
    const handleOpenDownloadModal = () => setOpenDownloadModal(true);
    const handleCloseDownloadModal = () => setOpenDownloadModal(false);
    const [loader, setLoader] = useState(false);


    const handleDownloadFromDateChange = (e) => {
        const newDate = dayjs(e.target.value);
        if (newDate.isValid() && newDate <= dayjs()) {
            setDownloadFromDate(newDate);
        }
    };
    const handleDownloadToDateChange = (e) => {
        const newDate = dayjs(e.target.value);
        if (newDate.isValid() && newDate <= dayjs()) {
            setDownloadToDate(newDate);
        }
    };


    const handleDownloadExcel = () => {

        if (!downloadFromDate || !downloadToDate) {
            toast.error('Please select both From Date and To Date for the download.');
            return;
        }
        setLoader(true);
        // The download function already expects standard Date objects, so no conversion needed here
        downloadTrainertAttendanceExcel(downloadFromDate, downloadToDate)
            .then(() => {

                handleCloseDownloadModal(); // Close modal on successful download initiation
            })
            .catch(error => {
                console.error("Download failed:", error);
                // The download function already shows an alert, but you can add more here
            }).finally(() => {
                setLoader(false);
            });
    };
    return (
        <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                    Trainer Attendance
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        label="Attendance Date"
                        type="date"
                        value={date.format('YYYY-MM-DD')}
                        onChange={handleDateChange}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            min: dayjs().format('YYYY-MM-DD')
                        }}
                        sx={{ width: 200 }}
                    />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={markAll(true)}
                            size="small"
                        >
                            Mark All Present
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={markAll(false)}
                            size="small"
                        >
                            Mark All Absent
                        </Button>
                    </Box>
                    <Button
                        variant="contained" // Use contained for a more prominent button
                        onClick={handleOpenDownloadModal}
                        size="small"
                        sx={{ ml: 2 }} // Margin-left to separate from other buttons
                    >
                        Download Excel
                    </Button>
                </Box>
                <Modal
                    open={openDownloadModal}
                    onClose={handleCloseDownloadModal}
                    aria-labelledby="download-excel-modal-title"
                    aria-describedby="download-excel-modal-description"
                >
                    <Paper sx={modalStyle}>
                        <Typography id="download-excel-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                            Select Date Range for Excel Download
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={downloadFromDate.format('YYYY-MM-DD')}
                                onChange={handleDownloadFromDateChange}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                            />
                            <TextField
                                label="To Date"
                                type="date"
                                value={downloadToDate.format('YYYY-MM-DD')}
                                onChange={handleDownloadToDateChange}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button variant="outlined" onClick={handleCloseDownloadModal}>
                                Cancel
                            </Button>
                            <Button variant="contained"
                                onClick={handleDownloadExcel}
                                disabled={loader}
                                startIcon={loader ? <CircularProgress size={20} /> : null}>
                                Download
                            </Button>
                        </Box>
                    </Paper>
                </Modal>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip
                    label={`Present: ${presentCount}`}
                    color="success"
                    variant="outlined"
                />
                <Chip
                    label={`Absent: ${absentCount}`}
                    color="error"
                    variant="outlined"
                />
                <Chip
                    label={`Total: ${trainers?.length}`}
                    variant="outlined"
                />
            </Box>

            {loading && <LinearProgress />}

            <Box display="flex" justifyContent="left" sx={{ mb: 2 }}>
                <TextField
                    label="Search Trainers"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    sx={{ width: '400px' }}
                />
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Trainer ID</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trainersToDisplay?.length > 0 ? (


                            trainersToDisplay?.map((trainer) => (
                                <TableRow key={trainer.id}>
                                    <TableCell>{trainer.id}</TableCell>
                                    <TableCell>{`${trainer.name}`}</TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={attendance[trainer.id] !== false}
                                            onClick={(e) => {
                                                setAttendance(prev => ({
                                                    ...prev,
                                                    [trainer.id]: !e.target.checked
                                                }));
                                            }}
                                            color="success"
                                        />
                                        <Typography variant="caption" sx={{ ml: 1 }}>
                                            {attendance[trainer.id] ? 'Present' : 'Absent'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))

                        ) : (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    {trainersToDisplay?.length === 0 ? 'No matching students found' : 'No students available'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>



            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || submitting}
                    size="large"
                >
                    {submitting ? 'Saving...' : 'Save Attendance'}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

export default TrainerAttendance;